import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import ProductGrid from '../components/ProductGrid';
import API from '../api';
import FilterSidebar from '../components/FilterSidebar';
import { MdViewModule, MdViewList } from 'react-icons/md';
import { useEventTracking } from '../hooks/useAnalytics';
import SEOMetadata from '../components/SEOMetadata';
import { CategorySkeleton } from '../components/skeleton';
import { generateCategorySEOMetadata } from '../config/seo';

const sortOptions = [
  { value: 'price_asc', label: 'Prezzo: dal più basso' },
  { value: 'price_desc', label: 'Prezzo: dal più alto' },
  { value: 'title_asc', label: 'Titolo: A-Z' },
  { value: 'title_desc', label: 'Titolo: Z-A' },
  { value: 'date_desc', label: 'Data: più recente' },
  { value: 'date_asc', label: 'Data: meno recente' },
];

const CategoryPage = () => {
  const { handle } = useParams();
  const { trackCategoryView, trackFilter } = useEventTracking();
  const [filters, setFilters] = useState({
    price: [0, 0],
    brands: [],
    vendors: [], // Cambiato da 'sellers' a 'vendors'
    sort: 'price_asc',
  });
  const [filterData, setFilterData] = useState({
    min_price: 0,
    max_price: 0,
    brands: [],
    vendors: [], // Cambiato da 'sellers' a 'vendors'
    total: 0,
    category_title: '',
  });
  const [view, setView] = useState('grid');
  const [filtersReady, setFiltersReady] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllVendors, setShowAllVendors] = useState(false); // Cambiato da 'showAllSellers'
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(null);
  const [categoryNotFound, setCategoryNotFound] = useState(false);

  // SEO Metadata per la categoria
  const seoData = filterData.category_title ? generateCategorySEOMetadata(filterData) : null;

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await API.get(`/categories/${handle}/filters`);
        if (res.data.success) {
          setFilterData({
            min_price: res.data.min_price || 0,
            max_price: res.data.max_price || 0,
            brands: res.data.brands || [],
            vendors: res.data.vendors || [], // Cambiato da 'sellers' a 'vendors'
            total: res.data.total || 0,
            category_title: res.data.category_title || '',
          });
          setFilters(f => ({
            ...f,
            price: [res.data.min_price || 0, res.data.max_price || 0],
          }));
          setFiltersReady(true);
        }
      } catch (e) {
        console.error('Errore durante il recupero della categoria:', e);
        // Se l'errore è 404 (categoria non trovata), imposta il flag
        if (e.response && e.response.status === 404) {
          setCategoryNotFound(true);
        }
        // fallback
        setFiltersReady(false);
      }
    };
    fetchFilters();
  }, [handle]); // Rimossa la dipendenza trackCategoryView

  // Traccia la visualizzazione della categoria quando i filtri sono pronti
  useEffect(() => {
    if (filtersReady && filterData.category_title) {
      trackCategoryView({
        id: handle,
        name: filterData.category_title
      }, 1);
    }
  }, [filtersReady, filterData.category_title, handle, trackCategoryView]);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFilters((prev) => ({
        ...prev,
        [name]: checked
          ? [...(Array.isArray(prev[name]) ? prev[name] : []), value]
          : (Array.isArray(prev[name]) ? prev[name] : []).filter((v) => v !== value),
      }));
      
      // Traccia l'applicazione del filtro
      if (checked) {
        trackFilter(name, value);
      }
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      
      // Traccia l'applicazione del filtro
      if (value) {
        trackFilter(name, value);
      }
    }
  };

  // Slider prezzo (input number per ora)
  const handlePriceChange = (e, idx) => {
    const val = Number(e.target.value);
    setFilters((prev) => {
      const newPrice = [...prev.price];
      newPrice[idx] = val;
      return { ...prev, price: newPrice };
    });
  };

  // Ordinamento
  const handleSortChange = (e) => {
    setFilters((prev) => ({ ...prev, sort: e.target.value }));
  };

  // Applica i filtri dal modal mobile
  const handleApplyMobileFilters = () => {
    if (pendingFilters) setFilters(pendingFilters);
    setShowMobileFilters(false);
  };

  // Se la categoria non è stata trovata, reindirizza alla pagina 404
  if (categoryNotFound) {
    return <Navigate to="/404" replace />;
  }

  return (
    <>
      {seoData && <SEOMetadata {...seoData} />}
      <div className="main-content-container category-page">
      <div className="category-page-header">
        <div className="category-page-title-section">
          <h1 className="category-page-title">{filterData.category_title}</h1>
          <div className="category-page-count">{filterData.total} prodotti</div>
        </div>
      </div>
      
      <div className="category-page-content">
        {/* Sidebar filtri (desktop) */}
        <div className="category-page-sidebar">
          <FilterSidebar
            filterData={filterData}
            filters={filters}
            onFilterChange={handleFilterChange}
            onPriceChange={handlePriceChange}
            showAllBrands={showAllBrands}
            setShowAllBrands={setShowAllBrands}
            showAllVendors={showAllVendors}
            setShowAllVendors={setShowAllVendors}
          />
        </div>
        
        {/* Modal filtri mobile */}
        {showMobileFilters && (
          <div className="mobile-filters-modal">
            <div className="mobile-filters-overlay" onClick={() => setShowMobileFilters(false)} />
            <div className="mobile-filters-sheet">
              <FilterSidebar
                filterData={filterData}
                filters={pendingFilters || filters}
                onFilterChange={e => {
                  const { name, value, type, checked } = e.target;
                  setPendingFilters(prev => {
                    if (!prev) return prev;
                    if (type === 'checkbox') {
                      return {
                        ...prev,
                        [name]: checked
                          ? [...(Array.isArray(prev[name]) ? prev[name] : []), value]
                          : (Array.isArray(prev[name]) ? prev[name] : []).filter((v) => v !== value),
                      };
                    } else {
                      return { ...prev, [name]: value };
                    }
                  });
                }}
                onPriceChange={(e, idx) => {
                  const val = Number(e.target.value);
                  setPendingFilters(prev => {
                    if (!prev) return prev;
                    const newPrice = [...prev.price];
                    newPrice[idx] = val;
                    return { ...prev, price: newPrice };
                  });
                }}
                showAllBrands={showAllBrands}
                setShowAllBrands={setShowAllBrands}
                showAllVendors={showAllVendors}
                setShowAllVendors={setShowAllVendors}
                isMobile={true}
                onApply={handleApplyMobileFilters}
                onClose={() => setShowMobileFilters(false)}
              />
            </div>
          </div>
        )}
        
        {/* Griglia/lista prodotti */}
        <div className="category-page-main">
          {/* Toolbar mobile (max-width: 991px) */}
          <div className="products-toolbar toolbar-mobile-custom">
            <div className="toolbar-mobile-row1">
              <button
                className="filter-btn"
                onClick={() => {
                  setPendingFilters({ ...filters });
                  setShowMobileFilters(true);
                }}
              >
                <span className="filter-icon" /> Filtri
              </button>
            </div>
            <div className="toolbar-mobile-row2">
              <div className="sort-pill">
                <select
                  className="sort-select"
                  name="sort"
                  value={filters.sort}
                  onChange={handleSortChange}
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className="sort-arrow">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M6 8l4 4 4-4" stroke="#c93249" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </div>
              <div className="view-toggle-pill">
                <div className="view-toggle-group">
                  <button
                    className={`view-toggle${view === 'grid' ? ' active' : ''}`}
                    onClick={() => setView('grid')}
                    type="button"
                    aria-label="Vista griglia"
                  >
                    <MdViewModule size={22} />
                  </button>
                  <button
                    className={`view-toggle${view === 'list' ? ' active' : ''}`}
                    onClick={() => setView('list')}
                    type="button"
                    aria-label="Vista lista"
                  >
                    <MdViewList size={22} />
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Toolbar desktop (min-width: 992px) */}
          <div className="products-toolbar toolbar-desktop-only">
            <label className="sort-label">Ordina per</label>
            <div className="sort-pill">
              <select
                className="sort-select"
                name="sort"
                value={filters.sort}
                onChange={handleSortChange}
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span className="sort-arrow">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M6 8l4 4 4-4" stroke="#c93249" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </div>
            <div className="view-toggle-pill">
              <div className="view-toggle-group">
                <button
                  className={`view-toggle${view === 'grid' ? ' active' : ''}`}
                  onClick={() => setView('grid')}
                  type="button"
                  aria-label="Vista griglia"
                >
                  <MdViewModule size={22} />
                </button>
                <button
                  className={`view-toggle${view === 'list' ? ' active' : ''}`}
                  onClick={() => setView('list')}
                  type="button"
                  aria-label="Vista lista"
                >
                  <MdViewList size={22} />
                </button>
              </div>
            </div>
          </div>
          {filtersReady && (
            <ProductGrid 
              categoryHandle={handle}
              filters={filters}
              view={view}
              onResultsLoaded={(totalResults, products) => {
                // Aggiorna il tracciamento della categoria con i prodotti
                if (filterData.category_title) {
                  const categoryData = {
                    id: handle,
                    name: filterData.category_title
                  };
                  
                  // Prepara gli items per il tracciamento
                  const items = products ? products.slice(0, 10).map(product => ({
                    item_id: product.id,
                    item_name: product.title,
                    item_category: filterData.category_title,
                    item_category_id: handle,
                    price: product.price_min || product.price
                  })) : [];
                  
                  trackCategoryView(categoryData, 1, items);
                }
              }}

            />
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default CategoryPage;
